import {
  useMutation,
  useQuery,
  useQueryClient,
  useIsRestoring,
} from "@tanstack/react-query";
import * as Crypto from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";
import { api } from "~/api/client";
import { useAuth } from "~/lib/auth";
import { backendConfigured } from "~/lib/constants";
import {
  GUEST_PROGRESS_QUERY_KEY,
  type GuestProgressMap,
} from "~/lib/guest-data";
import { recipeProgressQueryKey } from "~/lib/query-client";

// Row ids are chosen by the client. Two quick saves of a recipe with no row yet
// must target the same id (the API allows one row per person and recipe), so
// the id picked for a recipe is remembered for the session.
const pendingIds = new Map<string, string>();

export function useRecipeProgress(recipeId?: string) {
  const { user, hasPro } = useAuth();
  const queryClient = useQueryClient();
  // Free accounts (signed in or not) stay local-only; only Pro subscribers
  // get cross-device sync via the API.
  const useLocal = !backendConfigured || !user || !hasPro;
  // The persisted guest cache restores asynchronously; fetching before it finishes
  // would let the empty default overwrite saved data on reload.
  const isRestoring = useIsRestoring();

  const guestQuery = useQuery({
    queryKey: GUEST_PROGRESS_QUERY_KEY,
    queryFn: (): GuestProgressMap => ({}),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: useLocal && !isRestoring,
  });
  const guestProgress = guestQuery.data ?? {};
  const guestEntry = recipeId ? guestProgress[recipeId] : undefined;

  const remoteQuery = useQuery({
    queryKey: recipeProgressQueryKey(user?.id ?? "", recipeId ?? ""),
    enabled: Boolean(!useLocal && user && recipeId),
    queryFn: async () => {
      if (!user || !recipeId) return null;
      const list = await unwrap(api.listRecipeProgress());
      return list.items.find((row) => row.recipe_id === recipeId) ?? null;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: {
      completedSteps: number[];
      activeStep: number;
    }) => {
      if (!user || !recipeId) return;
      const id =
        remoteQuery.data?.id ??
        pendingIds.get(recipeId) ??
        (() => {
          const created = Crypto.randomUUID();
          pendingIds.set(recipeId, created);
          return created;
        })();
      await unwrap(
        api.putRecipeProgress({
          path: { id },
          body: {
            recipe_id: recipeId,
            completed_steps: payload.completedSteps,
            active_step: payload.activeStep,
          },
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: recipeProgressQueryKey(user?.id ?? "", recipeId ?? ""),
      });
    },
  });

  return {
    completedSteps: useLocal
      ? (guestEntry?.completedSteps ?? [])
      : (remoteQuery.data?.completed_steps ?? []),
    activeStep: useLocal
      ? (guestEntry?.activeStep ?? 0)
      : (remoteQuery.data?.active_step ?? 0),
    loading: useLocal ? guestQuery.isPending : remoteQuery.isPending,
    saveProgress: async (payload: {
      completedSteps: number[];
      activeStep: number;
    }) => {
      if (useLocal) {
        if (!recipeId) return;
        queryClient.setQueryData<GuestProgressMap>(
          GUEST_PROGRESS_QUERY_KEY,
          (current) => ({
            ...(current ?? {}),
            [recipeId]: payload,
          }),
        );
        return;
      }
      await saveMutation.mutateAsync(payload);
    },
  };
}
